import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
import { CommonPerformanceTableComponent } from '../../components/common-performance-table/common-performance-table.component';
import { SentimentTrendsComponent } from '../../components/sentiment-trends/sentiment-trends.component';

@Component({
  selector: 'app-generic-sentiment-tracking',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    BarLineComboChartComponent,
    AreaChartComponent,
    CompositeScoreWidgetComponent,
    // CitationCardComponent,
    CommonPerformanceTableComponent,
    SentimentTrendsComponent,
  ],
  templateUrl: './generic-sentiment-tracking.component.html',
  styleUrl: './generic-sentiment-tracking.component.scss'
})
export class GenericSentimentTrackingComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  
  // Core data properties
  sentimentTrackingResponse: any = null;
  data: any = null;
  genericBrands: any[] = [];
  compositeScore: any = null;

  // Visibility and memory management
  isTabVisible: boolean = false;
  isChartRendered: boolean = false;
  shouldRenderChart: boolean = false;

  // Chart properties
  results: any[] = [];
  lineChart: any[] = [];
  sentimentRadarData: any[] = [];

  // Table properties
  tableColumns: any[] = [];
  tableRows: any[] = [];
  sentimentTrackingTableColumns: any[] = [];
  sentimentTrackingTableRows: any[] = [];

  // Sentiment-specific data
  sentimentTrends: any = null;
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  // Color schemes for charts
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Responsive view dimensions (wider/taller for Sentiment Analysis Index chart)
  view: [number, number] = [800, 420];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  get firstBrandName(): string {
    const youBrand = this.findYourBrand(this.genericBrands || []);
    return youBrand ? youBrand.name : 'Your Brand';
  }

  hasData(): boolean {
    if (this.genericBrands.length === 0) return false;
    // New format may not have composite_score; we set it from brands, so check compositeScore or first brand score
    return !!this.compositeScore || (this.genericBrands.length > 0 && this.genericBrands[0].sentiment_score != null);
  }

  // Combo Chart Axis Scaling and Formatting - matching ngx-charts structure
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  // Chart event handlers
  onSelect(event: any): void {
    // Chart selection handler
  }

  onActivate(event: any): void {
    // Chart activation handler
  }

  onDeactivate(event: any): void {
    // Chart deactivation handler
  }

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}


  ngOnInit(): void {
    // Subscribe to sentiment tracking response
    const analysisSub = this.templateCommonService.sentimentTrackingResponse$.subscribe((response: any) => {
      if (response) {
        this.sentimentTrackingResponse = response;
        this.data = response;
        this.processSentimentAnalysisData(response);
        this.cdr.detectChanges();
      }
    });

    // Subscribe to filterData changes to detect competitor additions/removals
    let previousEntityNames: string[] = [];
    // Initialize from current filterData if available
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData && currentFilterData.selectedEntities) {
      previousEntityNames = currentFilterData.selectedEntities.map((e: any) => e.name || e).sort();
    }
    const filterDataSub = this.templateCommonService.filterData$.subscribe((filterData: any) => {
      // Do not auto-trigger API on Enter; only track names and wait for Analyze/Compare
      if (filterData && filterData.selectedEntities) {
        const currentEntityNames = filterData.selectedEntities.map((e: any) => e.name || e).sort();
        previousEntityNames = currentEntityNames;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, filterDataSub);

    // Load data when component initializes
    this.loadSentimentTrackingData();

    // Check visibility after component initialization
    setTimeout(() => {
      this.checkTabVisibility();
    }, 100);

    // Update responsive view dimensions
    this.updateViewByBreakpoint();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewByBreakpoint, { passive: true });
    }
  }

  ngOnDestroy(): void {
    console.log('🗑️ Sentiment Tracking: Destroying component and clearing memory...');
    
    // Clear chart data to free memory
    this.clearChartData();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.sentimentTrackingResponse = null;
    this.data = null;
    this.genericBrands = [];
    this.compositeScore = null;
    this.sentimentTrends = null;
    
    // Reset visibility flags
    this.isTabVisible = false;
    this.isChartRendered = false;
    this.shouldRenderChart = false;
    
    console.log('✅ Sentiment Tracking: Component destroyed and memory cleared');
  }

  /**
   * Clear chart data to reduce memory usage
   */
  private clearChartData(): void {
    this.results = [];
    this.lineChart = [];
    this.sentimentRadarData = [];
    this.isChartRendered = false;
    this.shouldRenderChart = false;
  }

  /**
   * Check if tab is visible using Intersection Observer
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkTabVisibility();
  }

  /**
   * Check tab visibility and manage chart rendering
   */
  private checkTabVisibility(): void {
    if (!this.elementRef?.nativeElement) return;
    
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    
    if (isVisible !== this.isTabVisible) {
      this.isTabVisible = isVisible;
      console.log(`🔍 Sentiment Tracking: Tab visibility changed to ${isVisible}`);
      
      if (isVisible) {
        this.onTabBecameVisible();
      } else {
        this.onTabBecameHidden();
      }
    }
  }

  /**
   * Handle tab becoming visible
   */
  private onTabBecameVisible(): void {
    console.log('👁️ Sentiment Tracking: Tab became visible, rendering chart...');
    this.shouldRenderChart = true;
    this.renderChartIfNeeded();
  }

  /**
   * Handle tab becoming hidden
   */
  private onTabBecameHidden(): void {
    console.log('🙈 Sentiment Tracking: Tab became hidden, clearing chart...');
    this.shouldRenderChart = false;
    this.clearChartData();
    this.cdr.detectChanges();
  }

  /**
   * Render chart only if tab is visible and data is available
   */
  private renderChartIfNeeded(): void {
    if (!this.shouldRenderChart || this.isChartRendered) return;
    const hasChartData = this.data && (this.data.competitor_analysis || this.data.chart_data || (this.genericBrands && this.genericBrands.length > 0));
    if (hasChartData) {
      console.log('📊 Sentiment Tracking: Rendering chart with data...');
      this.prepareChartData(this.data);
      this.isChartRendered = true;
      this.cdr.detectChanges();
    }
  }

  loadSentimentTrackingData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentSentimentTrackingResponse();
    if (currentResponse) {
      console.log('Sentiment Tracking: Found existing response, processing immediately');
      this.sentimentTrackingResponse = currentResponse;
      this.data = currentResponse;
      this.processSentimentAnalysisData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Sentiment Tracking: No data yet, waiting for subscription to receive data');
    }
  }

  // Method to trigger reload from parent component
  onReload(): void {
    this.templateCommonService.triggerTabReload('sentiment-tracking', true);
  }

  // Method to retry loading data (used in error state)
  retryLoad(): void {
    this.onReload();
  }

  private normalizeScore(v: any, lo: number, hi: number): number {
    if (v == null || v === '') return 0;
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (Number.isNaN(n)) return 0;
    return Math.max(lo, Math.min(hi, n));
  }

  /** True when data uses new format: chart_data (labels + overall_sentiment_scores or sentiment_scores) and/or brands[].overall_sentiment_score */
  private isNewSentimentFormat(data: any): boolean {
    if (!data) return false;
    const chartData = data.chart_data;
    const hasChartObject = chartData && typeof chartData === 'object' && !Array.isArray(chartData) && chartData !== null
      && !chartData.labels && Object.keys(chartData).length > 0 && typeof Object.values(chartData)[0] === 'number';
    const hasLabelsAndScores = chartData?.labels && (Array.isArray(chartData?.overall_sentiment_scores) || Array.isArray(chartData?.sentiment_scores));
    const hasNewChart = hasLabelsAndScores || hasChartObject;
    const firstBrand = data.brands?.[0];
    const hasNewBrandShape = firstBrand && (firstBrand.overall_sentiment_score != null || firstBrand.sentiment_score != null);
    return !!(hasNewChart || hasNewBrandShape);
  }

  private processSentimentAnalysisData(response: any): void {
    // Support both root-level data and structured_data (new API format)
    const data = response?.structured_data ?? response;
    this.data = data;

    if (this.isNewSentimentFormat(data)) {
      // New format: brands with overall_sentiment_score (sentiment), overall_reputation_score (reputation; backend may send this or brand_reputation_metrics)
      this.genericBrands = (data.brands || []).map((b: any) => {
        const repScore = this.normalizeScore(b.overall_reputation_score ?? b.brand_reputation_metrics, 0, 100);
        return {
          name: b.name,
          sentiment_score: b.overall_sentiment_score ?? b.sentiment_score ?? 0,
          overall_reputation_score: repScore,
          brand_reputation_metrics: b.brand_reputation_metrics,
          overall_reputation: typeof b.overall_reputation === 'number' ? b.overall_reputation : (b.overall_reputation ?? ''),
          social_media_sentiment: b.social_media_sentiment ?? 0,
          customer_satisfaction: b.customer_satisfaction ?? 0,
          news_sentiment: b.news_sentiment ?? 0,
          customer_perception: b.customer_perception,
          sentiment_trends: b.sentiment_trends,
          key_insights: b.key_insights || []
        };
      });
      const scores = (data.brands || []).map((b: any) => b.overall_sentiment_score ?? b.sentiment_score ?? 0).filter((n: number) => n != null);
      const avg = scores.length ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : 0;
      const firstBrand = data.brands?.[0];
      this.compositeScore = firstBrand
        ? {
            value: Math.round(avg),
            label: 'Sentiment Score',
            color: '#1976d2',
            brand: firstBrand.name
          }
        : null;
      this.sentimentTrends = data.sentiment_breakdown
        ? {
            positive_aspects: data.sentiment_breakdown.positive_aspects || data.sentiment_breakdown.positive || [],
            neutral_aspects: data.sentiment_breakdown.neutral_aspects || data.sentiment_breakdown.neutral || [],
            negative_aspects: data.sentiment_breakdown.negative_aspects || data.sentiment_breakdown.negative || []
          }
        : (() => {
            const trendStrings = (data.brands || []).map((b: any) => b.sentiment_trends).filter(Boolean);
            if (trendStrings.length) return { positive_aspects: [], neutral_aspects: trendStrings, negative_aspects: [] };
            return null;
          })();
      this.evidenceSources = this.transformSourcesData(data.evidence_sources || data.context?.citations || []);
      const recObj = data.recommendations || {};
      this.recommendations = typeof recObj === 'object' && !Array.isArray(recObj)
        ? Object.entries(recObj).map(([brand, recommendation]) => ({ brand, recommendation, category: 'general' }))
        : [];
    } else {
      // Legacy format
      this.genericBrands = data.brands || [];
      this.compositeScore = data.composite_score || null;
      this.sentimentTrends = data.sentiment_trends || null;
      this.evidenceSources = this.transformSourcesData(data.references?.sources || []);
      this.recommendations = Array.isArray(data.recommendations) ? data.recommendations : [];
    }

    this.prepareTableData(data);
    this.checkTabVisibility();
    // Always prepare chart data when we have data so chart is ready when tab is shown (no "Chart will load when tab becomes visible")
    this.prepareChartData(data);
    this.shouldRenderChart = this.results.length > 0 || this.lineChart.length > 0;
    this.cdr.markForCheck();
  }

  private prepareChartData(data: any): void {
    // Use the same pattern as AI Citations and Category Analysis
    if (!this.genericBrands || this.genericBrands.length === 0) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    // Use the same pattern as other working components
    this.prepareComboChartData();
  }

  private prepareComboChartData(): void {
    const data = this.data;
    const brands = this.genericBrands;
    const chartData = data?.chart_data;

    // Prefer chart_data.labels + overall_sentiment_scores (backend) or sentiment_scores – used by Sentiment Analysis Index chart
    const scoreArray = chartData && Array.isArray(chartData.overall_sentiment_scores)
      ? chartData.overall_sentiment_scores
      : chartData && Array.isArray(chartData.sentiment_scores)
        ? chartData.sentiment_scores
        : null;
    const labelsArray = chartData && Array.isArray(chartData.labels) ? chartData.labels : null;

    if (labelsArray && scoreArray && labelsArray.length === scoreArray.length) {
      this.results = labelsArray.map((name: string, index: number) => ({
        name: String(name),
        value: this.normalizeScore(scoreArray[index], 0, 100)
      }));
      this.lineChart = [];
      if (brands.length > 0) {
        console.log("===================>1brands:", brands);
        this.sentimentRadarData = brands.map((brand: any) => ({
          name: brand.name,
          series: [
            { name: 'Sentiment Score', value: this.normalizeScore(brand.sentiment_score ?? brand.overall_sentiment_score, 0, 100) },
            { name: 'Social Media', value: this.normalizeScore(brand.social_media_sentiment, 0, 100) },
            { name: 'Customer Satisfaction', value: this.normalizeScore(brand.customer_satisfaction, 0, 100) },
            { name: 'News Sentiment', value: this.normalizeScore(brand.news_sentiment, 0, 100) },
            // { name: 'Overall Reputation', value: this.normalizeScore(brand.overall_reputation_score ?? brand.overall_reputation ?? brand.brand_reputation_metrics, 0, 100) }
            { name: 'Overall Reputation', value: this.normalizeScore(brand.overall_reputation ?? 0, 0, 100) }
          ]
        }));
      }
      return;
    }

    // New format: chart_data as object { "BrandName": score } for initial bar chart (no labels array)
    if (this.isNewSentimentFormat(data) && chartData && typeof chartData === 'object' && !Array.isArray(chartData)) {
      if (chartData.labels && Array.isArray(chartData.sentiment_scores)) {
        const labels = chartData.labels as string[];
        const scores = chartData.sentiment_scores as number[];
        this.results = labels.map((name: string, index: number) => ({ name, value: this.normalizeScore(scores[index], 0, 100) }));
      } else {
        const entries = Object.entries(chartData).filter(([_, v]) => typeof v === 'number') as [string, number][];
        this.results = entries.map(([name, value]) => ({ name, value }));
      }
      this.lineChart = [];
      if (brands.length > 0) {
        console.log("===================>2brands:", brands);
        this.sentimentRadarData = brands.map((brand: any) => ({
          name: brand.name,
          series: [
            { name: 'Sentiment Score', value: brand.sentiment_score || 0 },
            { name: 'Social Media', value: brand.social_media_sentiment || 0 },
            { name: 'Customer Satisfaction', value: brand.customer_satisfaction || 0 },
            { name: 'News Sentiment', value: brand.news_sentiment || 0 },
            // { name: 'Overall Reputation', value: brand.overall_reputation_score ?? brand.overall_reputation ?? brand.brand_reputation_metrics ?? 0 }
            { name: 'Overall Reputation', value: brand.overall_reputation ?? 0 }
          ]
        }));
      }
      return;
    }

    // Legacy format: competitor_analysis or brand metrics
    const youBrand = this.findYouBrand(brands);
    const competitorBrands = this.findCompetitorBrands(brands);

    const xAxisLabels = data?.competitor_analysis?.x_axis || [
      'Sentiment Score',
      'Social Media Sentiment',
      'Customer Satisfaction',
      'News Sentiment',
      'Overall Reputation',
      'Brand Trust',
      'Emotional Connection',
      'Market Perception'
    ];

    if (youBrand) {
      this.results = xAxisLabels
        .map((label: string, index: number) => ({
          name: label,
          value: youBrand.sentiment_scores?.[index] || youBrand[this.getSentimentMetricKey(label)] || 0
        }))
        .filter((item: any) => item.value > 0);
    } else {
      this.results = [];
    }

    if (competitorBrands.length > 0) {
      this.lineChart = competitorBrands.map(brand => {
        const series = xAxisLabels
          .map((label: string, index: number) => ({
            name: label,
            value: brand.sentiment_scores?.[index] || brand[this.getSentimentMetricKey(label)] || 0
          }))
          .filter((item: any) => item.value > 0);
        return { name: brand.name, series };
      }).filter(brand => brand.series.length > 0);
    } else {
      this.lineChart = [];
    }

    if (brands.length > 0) {
      console.log("===================>3brands:", brands);
      this.sentimentRadarData = brands.map((brand: any) => ({
        name: brand.name,
        series: [
          { name: 'Sentiment Score', value: brand.sentiment_score || 0 },
          { name: 'Social Media', value: brand.social_media_sentiment || 0 },
          { name: 'Customer Satisfaction', value: brand.customer_satisfaction || 0 },
          { name: 'News Sentiment', value: brand.news_sentiment || 0 },
          // { name: 'Overall Reputation', value: brand.overall_reputation_score ?? brand.overall_reputation ?? brand.brand_reputation_metrics ?? 0 }
          { name: 'Overall Reputation', value: brand.overall_reputation ?? 0 }
        ]
      }));
    }
  }

  /**
   * Helper method to get sentiment metric key from label
   */
  private getSentimentMetricKey(label: string): string {
    const keyMap: { [key: string]: string } = {
      'Sentiment Score': 'sentiment_score',
      'Social Media Sentiment': 'social_media_sentiment',
      'Customer Satisfaction': 'customer_satisfaction',
      'News Sentiment': 'news_sentiment',
      'Overall Reputation': 'overall_reputation',
      'Brand Trust': 'brand_trust',
      'Emotional Connection': 'emotional_connection',
      'Market Perception': 'market_perception'
    };
    return keyMap[label] || 'sentiment_score';
  }

  /**
   * Helper methods following AI Citations pattern
   */
  private findYouBrand(brands: any[]): any {
    if (!brands || brands.length === 0) return null;
    
    // Look for brand marked as "your brand" or first brand
    const youBrand = brands.find(brand => brand.is_your_brand === true);
    return youBrand || brands[0];
  }

  private findCompetitorBrands(brands: any[]): any[] {
    if (!brands || brands.length <= 1) return [];
    
    // Return all brands except the "you" brand
    const youBrand = this.findYouBrand(brands);
    return brands.filter(brand => brand.name !== youBrand?.name);
  }

  private prepareTableData(data: any): void {
    // New format: Brand Sentiment Performance table with Sentiment Score, Social Media, Customer Satisfaction, News Sentiment, Overall Reputation
    if (this.isNewSentimentFormat(data) && data.brands?.length > 0) {
      this.tableRows = data.brands.map((b: any) => {
        const repScore = this.normalizeScore(b.overall_reputation_score ?? b.brand_reputation_metrics, 0, 100);
        const repVal = b.overall_reputation;
        const repLabel = (typeof repVal === 'string' ? repVal : repVal != null ? String(repVal) : '').trim() || (repScore >= 90 ? 'Excellent' : repScore >= 80 ? 'Very Good' : repScore >= 70 ? 'Good' : repScore >= 60 ? 'Fair' : 'Poor');
        return {
          brand: b.name,
          sentiment_score: b.overall_sentiment_score ?? b.sentiment_score ?? 0,
          social_media: b.social_media_sentiment ?? 0,
          customer_satisfaction: b.customer_satisfaction ?? 0,
          news_sentiment: b.news_sentiment ?? 0,
          overall_reputation_score: repScore,
          overall_reputation_display: repScore ? `${repScore} (${repLabel})` : (repLabel || 'N/A')
        };
      });
      this.tableColumns = [
        { key: 'brand', header: 'Brand', className: 'brand-column' },
        { key: 'sentiment_score', header: 'Sentiment Score', className: 'metric-column' },
        { key: 'social_media', header: 'Social Media', className: 'metric-column' },
        { key: 'customer_satisfaction', header: 'Customer Satisfaction', className: 'metric-column' },
        { key: 'news_sentiment', header: 'News Sentiment', className: 'metric-column' },
        { key: 'overall_reputation_display', header: 'Overall Reputation', className: 'metric-column', scoreKey: 'overall_reputation_score' }
      ];
      this.sentimentTrackingTableRows = [];
      this.sentimentTrackingTableColumns = [];
      return;
    }

    // Legacy: performance table
    if (data.performance_table && data.performance_table.length > 0) {
      this.tableRows = data.performance_table;
      this.tableColumns = [
        { key: 'brand', header: 'Brand', className: 'brand-column' },
        { key: 'sentiment_score', header: 'Sentiment Score', className: 'metric-column' },
        { key: 'social_media_sentiment', header: 'Social Media', className: 'metric-column' },
        { key: 'customer_satisfaction', header: 'Customer Satisfaction', className: 'metric-column' },
        { key: 'news_sentiment', header: 'News Sentiment', className: 'metric-column' },
        { key: 'overall_reputation', header: 'Overall Reputation', className: 'metric-column' }
      ];
    }

    // Legacy: sentiment tracking table
    if (data.sentiment_tracking_table && data.sentiment_tracking_table.length > 0) {
      this.sentimentTrackingTableRows = data.sentiment_tracking_table;
      this.sentimentTrackingTableColumns = [
        { key: 'brand', header: 'Brand', className: 'brand-column' },
        { key: 'service_quality', header: 'Service Quality', className: 'metric-column' },
        { key: 'customer_experience', header: 'Customer Experience', className: 'metric-column' },
        { key: 'financial', header: 'Financial', className: 'metric-column' },
        { key: 'value_proposition', header: 'Value Proposition', className: 'metric-column' },
        { key: 'trust_safety', header: 'Trust & Safety', className: 'metric-column' }
      ];
    }
  }




  // Helper methods following AI Citations pattern
  private findYourBrand(brands: any[]): any {
    if (!brands || brands.length === 0) return null;
    
    const youBrand = brands.find(brand => brand.is_your_brand === true);
    return youBrand || brands[0];
  }

  // Get chart subtitle
  getChartSubtitle(): string {
    if (!this.genericBrands || this.genericBrands.length === 0) return 'Sentiment Analysis';

    const brands = this.genericBrands;
    if (this.isNewSentimentFormat(this.data)) {
      return `Sentiment score by brand (${brands.length} brands)`;
    }
    if (brands.length === 1) {
      const brand = brands[0];
      return `📊 Single Brand Analysis: ${brand.name} - Detailed sentiment metrics breakdown`;
    }
    const youBrand = this.findYourBrand(brands);
    const competitorCount = brands.length - 1;
    if (youBrand) {
      return `Sentiment comparison: ${youBrand.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
    }
    return `Sentiment comparison across ${brands.length} brands`;
  }

  // Update responsive view dimensions
  private updateViewByBreakpoint = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (width <= 480) {
      this.view = [300, 300];
    } else if (width <= 768) {
      this.view = [400, 350];
    } else if (width <= 1024) {
      this.view = [500, 400];
    } else {
      this.view = [600, 400];
    }
    this.cdr.markForCheck();
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title,
      url: source.url,
      confidence: source.confidence,
      snippet: source.snippet || source.title,
      source: `Source ${index + 1}`, // Make each source unique
      category: 'sentiment_analysis',
      id: `source_${index}` // Add unique ID
    }));
  }

}
